import { Link } from 'react-router-dom';

export default function ContentSection() {
  return (
    <>
      <section className="w-full bg-background-100 py-14 md:py-20 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            <div>
              <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full mb-4 whitespace-nowrap">
                What Is
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                What Is Sugargoo Spreadsheet?
              </h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>
                  <strong className="text-foreground-900">Sugargoo Spreadsheet</strong> is a curated database designed to help shoppers discover trending products, compare options and understand quality before purchasing through the Sugargoo platform.
                </p>
                <p>
                  Instead of searching through thousands of products manually, users can quickly find organized collections including sneakers, fashion, accessories and popular items. The <strong className="text-foreground-900">Sugargoo Spreadsheet</strong> consolidates research into one accessible resource, saving hours of browsing time while helping shoppers make informed decisions about their purchases.
                </p>
                <p>
                  Whether you are a first-time Sugargoo user exploring the platform or an experienced shopper hunting for the latest seasonal finds, the spreadsheet format provides a structured way to navigate product listings, check quality references, and identify trending items across multiple categories.
                </p>
              </div>
              <div className="mt-6">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
                >
                  Browse Sugargoo Spreadsheet Database
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="w-full aspect-[4/3] rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20editorial%20workspace%20with%20a%20laptop%20on%20a%20clean%20wooden%20desk%20surrounded%20by%20stylish%20fashion%20accessories%20and%20curated%20products%2C%20soft%20golden%20hour%20natural%20light%20streaming%20through%20a%20window%2C%20cream%20and%20beige%20warm%20tones%2C%20elegant%20lifestyle%20photography%2C%20no%20people%2C%20shallow%20depth%20of%20field%2C%20sophisticated%20modern%20composition&width=800&height=600&seq=what-is-section-v2&orientation=landscape"
                  alt="Sugargoo Spreadsheet curated product research"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 py-14 md:py-20 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full mb-4 whitespace-nowrap">
              Why Choose
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              Why Use Sugargoo Spreadsheet?
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto leading-relaxed">
              Four key benefits that make the Sugargoo Spreadsheet an essential tool for smart shoppers.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {[
              {
                icon: 'ri-search-eye-line',
                title: 'Product Discovery',
                desc: 'Find trending products organized by category in one convenient location. The Sugargoo Spreadsheet brings together the most popular and noteworthy finds across all major categories.',
              },
              {
                icon: 'ri-magic-line',
                title: 'Quality Research',
                desc: 'Review QC information and quality indicators before committing to a purchase. The spreadsheet includes notes on product quality, helping you avoid disappointing buys.',
              },
              {
                icon: 'ri-scales-3-line',
                title: 'Price Comparison',
                desc: 'Compare products and alternatives side by side to find the best value. See pricing differences across sellers and categories at a glance.',
              },
              {
                icon: 'ri-book-open-line',
                title: 'Beginner Guidance',
                desc: 'Learn how Sugargoo shopping works with easy-to-follow resources. Perfect for newcomers who want to understand the platform before placing their first order.',
              },
            ].map((card) => (
              <div
                key={card.title}
                className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-7 hover:border-accent-200 transition-all duration-200"
              >
                <div className="w-11 h-11 rounded-full bg-accent-100 flex items-center justify-center mb-4">
                  <i className={`${card.icon} text-accent-600 text-xl`}></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                  {card.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {card.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 py-14 md:py-20 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4 whitespace-nowrap">
              Detailed Guide
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              Sugargoo Spreadsheet: The Complete Research Resource for Smart Shoppers
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto leading-relaxed">
              Everything you need to know about using the Sugargoo Spreadsheet to find better products and make smarter purchasing decisions.
            </p>
          </div>

          <article className="prose-custom space-y-6">
            <div>
              <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-3">
                How the Sugargoo Spreadsheet Transforms Your Shopping Experience
              </h3>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-3">
                The <strong className="text-foreground-900">Sugargoo Spreadsheet</strong> is more than just a list of products — it is a complete research system that helps shoppers navigate the vast Sugargoo marketplace with confidence. Each entry in the spreadsheet is carefully curated to include the most relevant information: product names, category tags, pricing details, and quality indicators. This organized approach eliminates the need to manually search through hundreds of individual product pages, saving shoppers significant time and effort.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-3">
                For shoppers who are new to the Sugargoo platform, the spreadsheet serves as an ideal starting point. It introduces the types of products available, demonstrates how categories are organized, and provides context that helps beginners understand what to expect when placing orders. Experienced users benefit from the spreadsheet as well — it becomes a reference tool for tracking seasonal trends, identifying reliable product sources, and comparing new finds against previously reviewed items.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                One of the standout features of the <strong className="text-foreground-900">Sugargoo Spreadsheet</strong> is its category-based organization. The spreadsheet covers major product groups including shoes, hoodies and sweaters, t-shirts, jackets, pants and shorts, headwear, matching sets, underwear, jerseys, accessories, bags, belts, and perfumes. This comprehensive coverage means shoppers can explore multiple product types within a single resource, making cross-category comparisons more efficient than ever.
              </p>
            </div>

            <div>
              <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-3">
                Making the Most of Your Sugargoo Spreadsheet Research
              </h3>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-3">
                To get the best results from the <strong className="text-foreground-900">Sugargoo Spreadsheet</strong>, start by identifying the categories that interest you most. The spreadsheet is designed to be browsed by category, so you can jump directly to shoes if you are looking for the latest sneaker releases, or head to the accessories section for smaller finds. Each category entry includes direct links that take you to the relevant product listings on the main site.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-3">
                Quality research is a critical part of the Sugargoo shopping process, and the spreadsheet supports this by including QC-related notes where available. When you browse a product through the spreadsheet, you are not just seeing a name and a price — you are getting context that helps you evaluate whether a particular item meets your quality expectations. This is especially valuable for categories like sneakers and fashion pieces, where subtle differences in materials and construction can significantly affect satisfaction.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                For shoppers who want to go deeper into their <strong className="text-foreground-900">Sugargoo Spreadsheet</strong> research, we have created a comprehensive guide that covers everything from beginner basics to advanced shopping strategies. Whether you want to understand shipping costs, learn about the warehouse system, or discover how to find the best discount codes, the full guide has you covered with detailed explanations and practical tips.
              </p>
            </div>

            <div className="pt-4 text-center">
              <Link
                to="/sugargoo-spreadsheet-guide"
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-white rounded-full text-sm font-semibold hover:bg-accent-600 transition-all duration-200 whitespace-nowrap"
              >
                Read the Complete Sugargoo Guide
                <i className="ri-article-line"></i>
              </Link>
            </div>
          </article>
        </div>
      </section>
    </>
  );
}